<template>
  <Transition name="book-bottom-controls">
    <div
      v-show="visible"
      class="book-bottom-controls"
      @click.stop
      @dblclick.stop
      @mousedown.stop
      @mouseup.stop
      @mouseenter.stop
      @mouseleave.stop
      @mousemove.stop
      @mouseover.stop
      @mouseout.stop
      @pointerenter.stop="emit('hoverChange', true)"
      @pointerleave.stop="emit('hoverChange', false)"
      @pointerdown.stop
      @pointermove.stop
      @pointerover.stop
      @pointerout.stop
      @pointerup.stop
      @touchstart.stop.passive
      @touchend.stop
      @wheel.stop
    >
      <div class="book-bottom-controls__nav" aria-label="Page navigation">
        <button
          type="button"
          class="book-bottom-controls__button"
          data-tooltip="上一页"
          aria-label="Previous page"
          :disabled="!canGoPrev"
          @mouseenter="setTooltipVisible($event, true)"
          @mouseleave="setTooltipVisible($event, false)"
          @touchstart.passive="setTooltipVisible($event, true)"
          @touchend="setTooltipVisible($event, false)"
          @touchcancel="setTooltipVisible($event, false)"
          @click="onButtonClick($event, () => emit('prev'))"
        >
          <BookControlIcon :name="BOOK_ICON_NAMES.BOOK_PREVIOUS" />
        </button>

        <button
          type="button"
          class="book-bottom-controls__button"
          :class="{ 'is-active': isPlaying }"
          :data-tooltip="isPlaying ? '暂停播放' : '自动播放'"
          :aria-label="isPlaying ? 'Pause autoplay' : 'Start autoplay'"
          :aria-pressed="isPlaying"
          @mouseenter="setTooltipVisible($event, true)"
          @mouseleave="setTooltipVisible($event, false)"
          @touchstart.passive="setTooltipVisible($event, true)"
          @touchend="setTooltipVisible($event, false)"
          @touchcancel="setTooltipVisible($event, false)"
          @click="onButtonClick($event, () => emit('togglePlay'))"
        >
          <BookControlIcon
            :name="isPlaying ? BOOK_ICON_NAMES.BOOK_PAUSE : BOOK_ICON_NAMES.BOOK_PLAY"
          />
        </button>

        <button
          type="button"
          class="book-bottom-controls__button"
          data-tooltip="下一页"
          aria-label="Next page"
          :disabled="!canGoNext"
          @mouseenter="setTooltipVisible($event, true)"
          @mouseleave="setTooltipVisible($event, false)"
          @touchstart.passive="setTooltipVisible($event, true)"
          @touchend="setTooltipVisible($event, false)"
          @touchcancel="setTooltipVisible($event, false)"
          @click="onButtonClick($event, () => emit('next'))"
        >
          <BookControlIcon :name="BOOK_ICON_NAMES.BOOK_NEXT" />
        </button>
      </div>

      <div
        class="book-bottom-controls__divider book-bottom-controls__divider--nav-page"
        aria-hidden="true"
      ></div>

      <div class="book-bottom-controls__page" :aria-label="pageStatusLabel">
        <span class="book-bottom-controls__page-current">{{ currentPageLabel }}</span>
        <span class="book-bottom-controls__page-slash">/</span>
        <span>{{ totalPages }}</span>
      </div>

      <div
        class="book-bottom-controls__divider book-bottom-controls__divider--page-tools"
        aria-hidden="true"
      ></div>

      <div class="book-bottom-controls__tools" aria-label="Book tools">
        <button
          type="button"
          class="book-bottom-controls__button"
          :class="{ 'is-active': isZoomed }"
          :data-tooltip="isZoomed ? '恢复' : '放大'"
          :aria-label="isZoomed ? 'Exit zoom' : 'Zoom in'"
          :aria-pressed="isZoomed"
          @mouseenter="setTooltipVisible($event, true)"
          @mouseleave="setTooltipVisible($event, false)"
          @touchstart.passive="setTooltipVisible($event, true)"
          @touchend="setTooltipVisible($event, false)"
          @touchcancel="setTooltipVisible($event, false)"
          @click="onButtonClick($event, () => emit('toggleZoom'))"
        >
          <BookControlIcon
            :name="isZoomed ? BOOK_ICON_NAMES.BOOK_ZOOM_RESTORE : BOOK_ICON_NAMES.BOOK_ZOOM"
          />
        </button>

        <div class="book-bottom-controls__sound">
          <div class="book-bottom-controls__volume" aria-hidden="true">
            <div class="book-bottom-controls__volume-card">
              <span class="book-bottom-controls__volume-value">{{ volumePercentLabel }}</span>
              <div
                ref="volumeTrackRef"
                class="book-bottom-controls__volume-track"
                @pointerdown="onVolumePointerDown"
                @pointermove="onVolumePointerMove"
                @pointerup="onVolumePointerUp"
                @pointercancel="onVolumePointerUp"
              >
                <div
                  class="book-bottom-controls__volume-fill"
                  :style="{ height: fillPercent }"
                ></div>
                <div
                  class="book-bottom-controls__volume-thumb"
                  :style="{ bottom: fillPercent }"
                ></div>
              </div>
            </div>
          </div>

          <button
            type="button"
            class="book-bottom-controls__button"
            :aria-label="isMuted ? 'Unmute' : 'Mute'"
            :aria-pressed="!isMuted"
            @click="onButtonClick($event, () => emit('toggleSound'))"
          >
            <BookControlIcon
              :name="isMuted ? BOOK_ICON_NAMES.BOOK_SOUND_OFF : BOOK_ICON_NAMES.BOOK_SOUND"
            />
          </button>
        </div>

        <button
          type="button"
          class="book-bottom-controls__button"
          :data-tooltip="isFullscreen ? '退出全屏' : '全屏'"
          :aria-label="isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'"
          @mouseenter="setTooltipVisible($event, true)"
          @mouseleave="setTooltipVisible($event, false)"
          @touchstart.passive="setTooltipVisible($event, true)"
          @touchend="setTooltipVisible($event, false)"
          @touchcancel="setTooltipVisible($event, false)"
          @click="onButtonClick($event, () => emit('toggleFullscreen'))"
        >
          <BookControlIcon
            :name="
              isFullscreen ? BOOK_ICON_NAMES.BOOK_FULLSCREEN_EXIT : BOOK_ICON_NAMES.BOOK_FULLSCREEN
            "
          />
        </button>
      </div>

      <div
        class="book-bottom-controls__divider book-bottom-controls__divider--tools-share"
        aria-hidden="true"
      ></div>

      <button
        type="button"
        class="book-bottom-controls__button"
        data-tooltip="分享"
        aria-label="Share"
        @mouseenter="setTooltipVisible($event, true)"
        @mouseleave="setTooltipVisible($event, false)"
        @touchstart.passive="setTooltipVisible($event, true)"
        @touchend="setTooltipVisible($event, false)"
        @touchcancel="setTooltipVisible($event, false)"
        @click="onButtonClick($event, () => emit('share'))"
      >
        <BookControlIcon :name="BOOK_ICON_NAMES.BOOK_SHARE" />
      </button>
    </div>
  </Transition>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, ref } from 'vue';
  import { storeToRefs } from 'pinia';
  import BookControlIcon from './BookControlIcon.vue';
  import { BOOK_ICON_NAMES } from '@/constants/svg.ts';
  import { useReaderControlsStore } from '@/store/readerControls';

  defineProps<{
    visible: boolean;
  }>();

  const {
    currentPageLabel,
    totalPages,
    canGoPrev,
    canGoNext,
    isFullscreen,
    muted: isMuted,
    isPlaying,
    isZoomed,
    effectiveVolume
  } = storeToRefs(useReaderControlsStore());

  const emit = defineEmits<{
    prev: [];
    next: [];
    share: [];
    toggleFullscreen: [];
    toggleZoom: [];
    togglePlay: [];
    toggleSound: [];
    setVolume: [value: number];
    hoverChange: [hovered: boolean];
  }>();

  const pageStatusLabel = computed(
    () => `第 ${currentPageLabel.value} 页，共 ${totalPages.value} 页`
  );

  const fillPercent = computed(() => `${effectiveVolume.value * 100}%`);
  const volumePercentLabel = computed(() => `${Math.round(effectiveVolume.value * 100)}`);

  const volumeTrackRef = ref<HTMLElement | null>(null);
  let dragging = false;

  /** 由指针在竖直轨道上的位置换算音量（顶部=1，底部=0）。 */
  function setVolumeFromPointer(clientY: number) {
    const track = volumeTrackRef.value;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const ratio = 1 - (clientY - rect.top) / rect.height;
    emit('setVolume', Math.max(0, Math.min(1, ratio)));
  }

  function onVolumePointerMove(event: PointerEvent) {
    if (dragging) setVolumeFromPointer(event.clientY);
  }

  function onVolumePointerUp(event: PointerEvent) {
    dragging = false;
    volumeTrackRef.value?.releasePointerCapture(event.pointerId);
  }

  // 父容器对 pointermove/pointerup 调用了 stopPropagation 以屏蔽画布交互，
  // window 监听收不到冒泡事件；改用指针捕获，事件直接定向到轨道元素自身。
  function onVolumePointerDown(event: PointerEvent) {
    event.preventDefault();
    dragging = true;
    setVolumeFromPointer(event.clientY);
    volumeTrackRef.value?.setPointerCapture(event.pointerId);
  }

  onBeforeUnmount(() => {
    dragging = false;
  });

  /** 悬停或按住时显示 tooltip */
  function setTooltipVisible(event: Event, visible: boolean) {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    button.classList.toggle('is-tooltip-visible', visible);
  }

  /** 点击执行操作并收起 tooltip，避免点击后残留 */
  function onButtonClick(event: MouseEvent, handler: () => void) {
    handler();
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;
    button.classList.remove('is-tooltip-visible');
    button.blur();
  }
</script>

<style scoped>
  .book-bottom-controls {
    position: absolute;
    left: 0;
    right: 0;
    z-index: 10;
    width: fit-content;
    min-width: 356px;
    height: 42px;
    max-width: calc(100% - 24px);
    margin-inline: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: 8px 16px;
    color: #160211;
    background: #fff;
    border: 0.8px solid rgba(0, 0, 0, 0.1);
    border-radius: 999px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    overflow: visible;
    user-select: none;
    box-sizing: border-box;
  }

  .book-bottom-controls__nav,
  .book-bottom-controls__tools {
    display: flex;
    align-items: center;
    gap: 0;
  }

  .book-bottom-controls__button {
    --tooltip-bg: #070a0f;
    --tooltip-fg: #fff;
    position: relative;
    width: 30px;
    height: 30px;
    flex: 0 0 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #160211;
    background: transparent;
    border: 0;
    border-radius: 50%;
    padding: 0;
    cursor: pointer;
    touch-action: manipulation;
    transition:
      transform 160ms ease,
      background-color 160ms ease,
      opacity 160ms ease;
  }

  .book-bottom-controls__button.is-active {
    background: rgba(22, 2, 17, 0.08);
  }

  .book-bottom-controls__button:hover:not(:disabled) {
    background: rgba(22, 2, 17, 0.06);
  }

  .book-bottom-controls__button:focus-visible {
    outline: 2px solid #160211;
    outline-offset: 3px;
  }

  .book-bottom-controls__button:disabled {
    opacity: 0.25;
    cursor: not-allowed;
  }

  /* ===== 音量控制（悬停在音频按钮上方弹出竖向滑条） ===== */
  .book-bottom-controls__sound {
    position: relative;
    display: inline-flex;
  }

  .book-bottom-controls__volume {
    position: absolute;
    left: 50%;
    bottom: 100%;
    /* 下内边距充当按钮与卡片之间的悬停桥，避免移动到滑条时丢失 hover */
    padding-bottom: 10px;
    transform: translate(-50%, 6px);
    opacity: 0;
    visibility: hidden;
    transition:
      opacity 160ms ease,
      transform 160ms ease;
    z-index: 30;
  }

  .book-bottom-controls__sound:hover .book-bottom-controls__volume,
  .book-bottom-controls__sound:focus-within .book-bottom-controls__volume,
  .book-bottom-controls__volume:hover {
    opacity: 1;
    visibility: visible;
    transform: translate(-50%, 0);
  }

  .book-bottom-controls__volume-card {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 12px 0 14px;
    width: 40px;
    background: #fff;
    border: 0.8px solid rgba(0, 0, 0, 0.1);
    border-radius: 14px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.14);
  }

  /* 卡片底部朝下的小箭头 */
  .book-bottom-controls__volume-card::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 7px 7px 0;
    border-color: #fff transparent transparent;
    transform: translateX(-50%);
    filter: drop-shadow(0 2px 1px rgba(0, 0, 0, 0.06));
  }

  .book-bottom-controls__volume-value {
    color: #160211;
    font-family: Nunito, system-ui, sans-serif;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    opacity: 0.7;
  }

  .book-bottom-controls__volume-track {
    position: relative;
    width: 5px;
    height: 96px;
    border-radius: 999px;
    background: rgba(22, 2, 17, 0.12);
    cursor: pointer;
    touch-action: none;
  }

  .book-bottom-controls__volume-fill {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    background: #160211;
    border-radius: 999px;
  }

  .book-bottom-controls__volume-thumb {
    position: absolute;
    left: 50%;
    width: 13px;
    height: 13px;
    border-radius: 50%;
    background: #fff;
    border: 2px solid #160211;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
    transform: translate(-50%, 50%);
    transition: transform 120ms ease;
  }

  .book-bottom-controls__volume-track:hover .book-bottom-controls__volume-thumb {
    transform: translate(-50%, 50%) scale(1.12);
  }

  .book-bottom-controls__divider {
    width: 1px;
    height: 16px;
    flex: 0 0 1px;
    margin: 0 4px;
    background: rgba(0, 0, 0, 0.12);
  }

  .book-bottom-controls__page {
    flex: 0 0 auto;
    min-width: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #160211;
    font-family: Nunito, system-ui, sans-serif;
    font-size: 14px;
    font-weight: 400;
    font-style: normal;
    font-synthesis: none;
    line-height: 20px;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .book-bottom-controls__page-current {
    font-weight: 700;
    font-style: normal;
  }

  .book-bottom-controls__page-slash {
    margin: 0 4px;
    color: #160211;
    font-weight: 400;
    opacity: 0.4;
  }

  .book-bottom-controls__button::before,
  .book-bottom-controls__button::after {
    position: absolute;
    left: 50%;
    opacity: 0;
    pointer-events: none;
    transition:
      opacity 140ms ease,
      transform 140ms ease;
  }

  .book-bottom-controls__button::before {
    content: attr(data-tooltip);
    bottom: calc(100% + 14px);
    z-index: 20;
    padding: 7px 12px;
    color: var(--tooltip-fg);
    background: var(--tooltip-bg);
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.35;
    white-space: nowrap;
    transform: translate(-50%, 6px);
  }

  .book-bottom-controls__button::after {
    content: '';
    bottom: calc(100% + 4px);
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 10px 10px 0;
    border-color: var(--tooltip-bg) transparent transparent;
    transform: translate(-50%, 6px);
  }

  .book-bottom-controls__button.is-tooltip-visible:not(:disabled)::before,
  .book-bottom-controls__button.is-tooltip-visible:not(:disabled)::after {
    opacity: 1;
    transform: translate(-50%, 0);
  }

  .book-bottom-controls-enter-active,
  .book-bottom-controls-leave-active {
    transition:
      opacity 200ms ease,
      transform 200ms ease;
  }

  .book-bottom-controls-enter-from,
  .book-bottom-controls-leave-to {
    opacity: 0;
    transform: translateY(8px);
  }

  .book-bottom-controls-enter-to,
  .book-bottom-controls-leave-from {
    opacity: 1;
    transform: translateY(0);
  }

  @media (prefers-reduced-motion: reduce) {
    .book-bottom-controls__button,
    .book-bottom-controls__button::before,
    .book-bottom-controls__button::after,
    .book-bottom-controls__volume,
    .book-bottom-controls__volume-thumb,
    .book-bottom-controls-enter-active,
    .book-bottom-controls-leave-active {
      transition: none;
    }
  }
</style>
